import React from 'react';
import { Recipe } from '../../types/Recipe';

interface RecipeFormProps {
  initialData?: Partial<Recipe>;
  onSubmit: (data: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>) => void;
  isEditing?: boolean;
}

const RecipeForm: React.FC<RecipeFormProps> = ({
  initialData = {},
  onSubmit,
  isEditing = false
}) => {
  const [title, setTitle] = React.useState(initialData.title || '');
  const [description, setDescription] = React.useState(initialData.description || '');
  const [category, setCategory] = React.useState<Recipe['category']>(initialData.category || 'plat');
  const [prepTime, setPrepTime] = React.useState(initialData.prepTime || 0);
  const [cookTime, setCookTime] = React.useState(initialData.cookTime || 0);
  const [servings, setServings] = React.useState(initialData.servings || 2);
  const [difficulty, setDifficulty] = React.useState<Recipe['difficulty']>(initialData.difficulty || 'facile');
  const [imageUrl, setImageUrl] = React.useState(initialData.imageUrl || '');
  const [ingredients, setIngredients] = React.useState<string[]>(initialData.ingredients || []);
  const [steps, setSteps] = React.useState<string[]>(initialData.steps || []);
  
  // État pour les nouveaux ingrédients et étapes
  const [newIngredient, setNewIngredient] = React.useState('');
  const [newStep, setNewStep] = React.useState('');
  
  // Gestion de l'image
  const [previewImage, setPreviewImage] = React.useState<string | null>(initialData.imageUrl || null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewImage(result);
        setImageUrl(result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleAddIngredient = () => {
    if (newIngredient.trim()) {
      setIngredients([...ingredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };
  
  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };
  
  const handleAddStep = () => {
    if (newStep.trim()) {
      setSteps([...steps, newStep.trim()]);
      setNewStep('');
    }
  };
  
  const handleRemoveStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation de base
    if (!title.trim()) {
      alert('Le titre est obligatoire');
      return;
    }
    
    if (ingredients.length === 0) {
      alert('Ajoutez au moins un ingrédient');
      return;
    }
    
    if (steps.length === 0) {
      alert('Ajoutez au moins une étape de préparation');
      return;
    }
    
    // Préparation des données
    const recipeData: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'> = {
      title,
      description,
      category,
      prepTime,
      cookTime,
      servings,
      difficulty,
      ingredients,
      steps,
      imageUrl
    };
    
    onSubmit(recipeData);
  };
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6 text-center">
        {isEditing ? 'Modifier la recette' : 'Ajouter une nouvelle recette'}
      </h2>
      
      {/* Upload Photo */}
      <div className="mb-8">
        <label className="block text-gray-700 font-medium mb-2">Photo de la recette</label>
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {previewImage ? (
            <div className="relative">
              <img 
                src={previewImage} 
                alt="Aperçu" 
                className="max-h-64 mx-auto rounded-lg"
              />
              <button 
                type="button"
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage(null);
                  setImageUrl('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="py-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500">Cliquez pour ajouter une photo ou glissez-déposez une image</p>
            </div>
          )}
          <input 
            type="file" 
            ref={fileInputRef}
            className="hidden" 
            accept="image/*"
            onChange={handleImageChange}
          />
        </div>
      </div>
      
      {/* Informations de base */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label htmlFor="title" className="block text-gray-700 font-medium mb-2">Titre de la recette *</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input-field"
            placeholder="Ex: Tarte aux pommes maison"
            required
          />
        </div>
        
        <div>
          <label htmlFor="category" className="block text-gray-700 font-medium mb-2">Catégorie *</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as Recipe['category'])}
            className="input-field"
            required
          >
            <option value="entrée">Entrée</option>
            <option value="plat">Plat</option>
            <option value="dessert">Dessert</option>
            <option value="boisson">Boisson</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="prepTime" className="block text-gray-700 font-medium mb-2">Temps de préparation (min) *</label>
          <input
            type="number"
            id="prepTime"
            value={prepTime}
            onChange={(e) => setPrepTime(parseInt(e.target.value) || 0)}
            className="input-field"
            min="0"
            required
          />
        </div>
        
        <div>
          <label htmlFor="cookTime" className="block text-gray-700 font-medium mb-2">Temps de cuisson (min)</label>
          <input
            type="number"
            id="cookTime"
            value={cookTime}
            onChange={(e) => setCookTime(parseInt(e.target.value) || 0)}
            className="input-field"
            min="0"
          />
        </div>
        
        <div>
          <label htmlFor="servings" className="block text-gray-700 font-medium mb-2">Nombre de personnes *</label>
          <input
            type="number"
            id="servings"
            value={servings}
            onChange={(e) => setServings(parseInt(e.target.value) || 1)}
            className="input-field"
            min="1"
            required
          />
        </div>
        
        <div>
          <label className="block text-gray-700 font-medium mb-2">Difficulté *</label>
          <div className="flex space-x-4">
            {['facile', 'moyen', 'difficile'].map((level) => (
              <label key={level} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="difficulty"
                  value={level}
                  checked={difficulty === level}
                  onChange={() => setDifficulty(level as Recipe['difficulty'])}
                  className="sr-only"
                />
                <div className={`px-4 py-2 rounded-full ${difficulty === level ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
      
      {/* Description */}
      <div className="mb-8">
        <label htmlFor="description" className="block text-gray-700 font-medium mb-2">Description</label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input-field min-h-[100px]"
          placeholder="Décrivez votre recette en quelques mots..."
        />
      </div>
      
      {/* Ingrédients */}
      <div className="mb-8">
        <label className="block text-gray-700 font-medium mb-2">Ingrédients *</label>
        <div className="flex mb-2">
          <input
            type="text"
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            className="input-field mr-2"
            placeholder="Ex: 200g de farine"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIngredient())}
          />
          <button
            type="button"
            onClick={handleAddIngredient}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Ajouter
          </button>
        </div>
        
        {ingredients.length > 0 ? (
          <ul className="bg-gray-50 rounded-lg p-4 space-y-2">
            {ingredients.map((ingredient, index) => (
              <li key={index} className="flex justify-between items-center bg-white p-2 rounded-md shadow-sm">
                <span>{ingredient}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveIngredient(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic">Aucun ingrédient ajouté</p>
        )}
      </div>
      
      {/* Étapes de préparation */}
      <div className="mb-8">
        <label className="block text-gray-700 font-medium mb-2">Étapes de préparation *</label>
        <div className="flex mb-2">
          <textarea
            value={newStep}
            onChange={(e) => setNewStep(e.target.value)}
            className="input-field mr-2 min-h-[80px]"
            placeholder="Décrivez une étape de préparation..."
          />
          <button
            type="button"
            onClick={handleAddStep}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors h-fit"
          >
            Ajouter
          </button>
        </div>
        
        {steps.length > 0 ? (
          <ol className="bg-gray-50 rounded-lg p-4 space-y-4">
            {steps.map((step, index) => (
              <li key={index} className="flex items-start bg-white p-3 rounded-md shadow-sm">
                <span className="flex-shrink-0 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                  {index + 1}
                </span>
                <div className="flex-grow">
                  <p>{step}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveStep(index)}
                  className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-gray-500 italic">Aucune étape ajoutée</p>
        )}
      </div>
      
      {/* Bouton de soumission */}
      <div className="text-center">
        <button
          type="submit"
          className="btn-primary py-3 px-8 text-lg"
        >
          {isEditing ? 'Mettre à jour la recette' : 'Enregistrer la recette'}
        </button>
      </div>
    </form>
  );
};

export default RecipeForm;
